import React, { useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import type { SpaceType } from '@/pages/Settings/UserManagement/SpaceAssignments';

interface SpaceAccessGuardProps {
  children: React.ReactNode;
}

/**
 * SpaceAccessGuard - Checks URL parameters for space IDs and validates user access.
 * If the user doesn't have access to the space in the URL, redirects to home.
 * If user has no access at all, still renders the dashboard (with empty sidebar).
 *
 * Only the most specific (leaf) space in the URL is checked. When roomId or
 * zoneId is present, spaceId is just the parent floor context needed for
 * rendering — a user with room/zone access can view the room/zone page without
 * needing separate floor-level access.
 *
 * Note: a manually-crafted URL could pair a roomId with an unrelated spaceId.
 * The backend GraphQL resolvers enforce per-object authorization, so metadata
 * from a mismatched floor/building will not be returned to unauthorized users.
 */
export const SpaceAccessGuard: React.FC<SpaceAccessGuardProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccess, isLoading, isUnrestricted } = useSpaceAccess();

  // Determine the single leaf space to check from URL params.
  // Hierarchy priority: roomId > zoneId > spaceId (floor) > buildingId.
  const spaceToCheck = useMemo((): { type: SpaceType; id: string } | null => {
    const params = new URLSearchParams(location.search);

    const roomId = params.get('roomId');
    if (roomId) return { type: 'room', id: roomId };

    const zoneId = params.get('zoneId');
    if (zoneId) return { type: 'zone', id: zoneId };

    const spaceId = params.get('spaceId');
    if (spaceId) return { type: 'floor', id: spaceId };

    const buildingId = params.get('buildingId');
    if (buildingId) return { type: 'building', id: buildingId };

    return null;
  }, [location.search]);

  const isBlocked = useMemo(() => {
    if (isLoading || isUnrestricted || !spaceToCheck) return false;
    return !hasAccess(spaceToCheck.type, spaceToCheck.id);
  }, [isLoading, isUnrestricted, spaceToCheck, hasAccess]);

  // Redirect to home if user doesn't have access to the space in the URL
  useEffect(() => {
    if (isBlocked && !isLoading) {
      navigate('/');
    }
  }, [isBlocked, isLoading, navigate]);

  // Don't render children until access is verified to prevent flash of protected content
  if (isLoading || isBlocked) {
    return null;
  }

  return <>{children}</>;
};

export default SpaceAccessGuard;
